import { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { UserPlus, Mail, Loader2, CheckCircle2, Send, Link as LinkIcon, MessageSquare, Share2, Copy } from "lucide-react";
import { toast } from "sonner";

/**
 * Card that lets a signed-in user (customer or business) invite someone to
 * create a customer account on Servrevu. Posts to `/api/me/invite-customer`,
 * which sends a Resend email and tracks the invite in `customer_invites`.
 *
 * Renders a small "Sent invites" history below the form.
 */
export default function InviteCustomerCard() {
  const { user } = useAuth();
  const [form, setForm] = useState({ email: "", name: "", message: "" });
  const [sending, setSending] = useState(false);
  const [invites, setInvites] = useState([]);
  const [loadedHistory, setLoadedHistory] = useState(false);

  // Personalised invite link — used by SMS / social / copy-link buttons. The
  // backend (`/auth/register?invited_by=…`) consumes the param to log the invite
  // back to the inviter so it appears as "Joined" in the history below.
  const inviteLink = useMemo(() => {
    if (!user?.id) return "";
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/register?role=customer&invited_by=${user.id}`;
  }, [user?.id]);

  const shareMessage = useMemo(() => {
    const inviter = (user?.business_name || user?.name || "I").trim();
    return `${inviter} thinks you'll like Servrevu — a two-sided local business directory where customers and businesses review each other. Create a free customer account: ${inviteLink}`;
  }, [user, inviteLink]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/me/invites");
      setInvites(data.items || []);
    } catch (e) {
      console.warn("invite history load failed:", e);
    } finally {
      setLoadedHistory(true);
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) return;
    setSending(true);
    try {
      const { data } = await api.post("/me/invite-customer", {
        email: form.email.trim(),
        name: form.name.trim(),
        message: form.message.trim(),
      });
      if (data.delivered) {
        toast.success("Invite sent — we'll let you know when they sign up.");
      } else {
        toast.message("Invite logged", { description: "Email could not be delivered, but we'll remember the invite." });
      }
      setForm({ email: "", name: "", message: "" });
      loadHistory();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="rounded-md border border-border p-6" data-testid="invite-customer-card">
      <div className="flex items-center gap-2 flex-wrap">
        <UserPlus size={18} className="text-[#B31E1E]" />
        <h3 className="font-display text-2xl">Invite a friend to Servrevu</h3>
      </div>
      <p className="text-sm text-gray-500 mt-1 max-w-2xl">
        Message someone a personal invite to join Servrevu as a customer. They&apos;ll get a warm welcome from you with a one-click signup link — no spammy &quot;you&apos;ve been added&quot; surprise.
      </p>

      <form onSubmit={submit} className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="invite-customer-form">
        <div>
          <Label htmlFor="inv-email">Their email</Label>
          <Input
            id="inv-email"
            type="email"
            required
            placeholder="friend@example.com"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="rounded-md mt-1"
            data-testid="invite-email-input"
          />
        </div>
        <div>
          <Label htmlFor="inv-name">Their first name <span className="text-gray-400 font-normal">(optional)</span></Label>
          <Input
            id="inv-name"
            type="text"
            maxLength={120}
            placeholder="Sam"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="rounded-md mt-1"
            data-testid="invite-name-input"
          />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="inv-msg">Personal note <span className="text-gray-400 font-normal">(optional)</span></Label>
          <Textarea
            id="inv-msg"
            rows={3}
            maxLength={600}
            placeholder="I've been using Servrevu to find great local places — thought you'd love it too."
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            className="rounded-md mt-1"
            data-testid="invite-message-input"
          />
          <p className="text-[11px] text-gray-500 mt-1">Shown as a quote inside the invite message.</p>
        </div>
        <div className="sm:col-span-2">
          <Button
            type="submit"
            disabled={sending || !form.email}
            className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]"
            data-testid="invite-submit-button"
          >
            {sending ? (<><Loader2 size={14} className="mr-1.5 animate-spin" /> Sending…</>) : (<><Send size={14} className="mr-1.5" /> Send invite</>)}
          </Button>
        </div>
      </form>

      <ShareInviteRow inviteLink={inviteLink} shareMessage={shareMessage} />

      {loadedHistory && invites.length > 0 && (
        <div className="mt-6 border-t border-border pt-5" data-testid="invite-history">
          <div className="flex items-center gap-2">
            <Mail size={13} className="text-gray-400" />
            <span className="overline text-gray-500">Sent invites</span>
            <Badge variant="outline" className="rounded-sm text-[10px] font-mono">{invites.length}</Badge>
          </div>
          <ul className="mt-3 space-y-1.5">
            {invites.map((i) => (
              <li
                key={i.id}
                className="flex items-center justify-between text-sm gap-3 px-3 py-2 rounded-md hover:bg-gray-50"
                data-testid={`invite-row-${i.id}`}
              >
                <div className="min-w-0">
                  <p className="text-gray-900 truncate">
                    {i.invitee_name ? <span className="font-medium">{i.invitee_name} · </span> : null}
                    <span className="font-mono text-xs text-gray-600">{i.invitee_email}</span>
                  </p>
                  <p className="text-[11px] font-mono text-gray-400 mt-0.5">
                    {new Date(i.created_at).toLocaleString()}
                  </p>
                </div>
                {i.accepted ? (
                  <Badge className="rounded-sm bg-emerald-500 hover:bg-emerald-500 text-white text-[10px] font-mono">
                    <CheckCircle2 size={10} className="mr-1" /> Joined
                  </Badge>
                ) : i.delivered ? (
                  <Badge variant="outline" className="rounded-sm border-[#B31E1E] text-[#B31E1E] text-[10px] font-mono">
                    Sent
                  </Badge>
                ) : (
                  <Badge variant="outline" className="rounded-sm border-amber-400 text-amber-700 text-[10px] font-mono">
                    Email skipped
                  </Badge>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}


function ShareInviteRow({ inviteLink, shareMessage }) {
  if (!inviteLink) return null;

  const encoded = encodeURIComponent(shareMessage);
  const encodedLink = encodeURIComponent(inviteLink);
  const supportsNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  const channels = [
    {
      id: "sms",
      label: "Text",
      // SMS deep link — works on iOS + Android. The `&body=` syntax is widely supported.
      href: `sms:?&body=${encoded}`,
      brandClasses: "bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600",
    },
    {
      id: "whatsapp",
      label: "WhatsApp",
      href: `https://wa.me/?text=${encoded}`,
      brandClasses: "bg-[#25D366] hover:bg-[#1ebe57] text-white border-[#25D366]",
    },
    {
      id: "messenger",
      label: "Messenger",
      // Facebook Messenger web share — opens a chooser to send to any contact.
      href: `https://www.facebook.com/dialog/send?link=${encodedLink}&app_id=140586622674265&redirect_uri=${encodedLink}`,
      brandClasses: "bg-[#0078FF] hover:bg-[#0065d6] text-white border-[#0078FF]",
    },
    {
      id: "x",
      label: "X / Twitter",
      href: `https://twitter.com/intent/tweet?text=${encoded}`,
      brandClasses: "bg-black hover:bg-gray-800 text-white border-black",
    },
    {
      id: "facebook",
      label: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedLink}&quote=${encoded}`,
      brandClasses: "bg-[#1877F2] hover:bg-[#0f62cb] text-white border-[#1877F2]",
    },
    {
      id: "linkedin",
      label: "LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedLink}`,
      brandClasses: "bg-[#0A66C2] hover:bg-[#085196] text-white border-[#0A66C2]",
    },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      toast.success("Invite link copied");
    } catch {
      toast.error("Couldn't copy — long-press the link below to copy manually");
    }
  };

  const nativeShare = async () => {
    try {
      await navigator.share({ title: "Join me on Servrevu", text: shareMessage, url: inviteLink });
    } catch (e) {
      // user cancelled or unsupported — silently ignore
      if (e?.name !== "AbortError") console.warn("native share failed:", e);
    }
  };

  return (
    <div className="mt-6 border-t border-border pt-5" data-testid="invite-share-row">
      <div className="flex items-center gap-2 flex-wrap">
        <LinkIcon size={13} className="text-gray-400" />
        <span className="overline text-gray-500">Or share by text / social</span>
      </div>

      <div className="mt-3 flex items-stretch gap-2 flex-wrap" data-testid="invite-link-bar">
        <Input
          readOnly
          value={inviteLink}
          onFocus={(e) => e.target.select()}
          className="rounded-md font-mono text-xs flex-1 min-w-[220px]"
          data-testid="invite-link-input"
        />
        <Button
          type="button"
          variant="outline"
          onClick={copy}
          className="rounded-[35px] shrink-0"
          data-testid="invite-link-copy"
        >
          <Copy size={13} className="mr-1.5" /> Copy
        </Button>
        {supportsNativeShare && (
          <Button
            type="button"
            variant="outline"
            onClick={nativeShare}
            className="rounded-[35px] shrink-0"
            data-testid="invite-native-share"
          >
            <Share2 size={13} className="mr-1.5" /> Share…
          </Button>
        )}
      </div>

      <div className="mt-3 flex flex-wrap gap-2" data-testid="invite-channels">
        {channels.map((c) => (
          <a
            key={c.id}
            href={c.href}
            target={c.id === "sms" ? "_self" : "_blank"}
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-1.5 rounded-[35px] border px-3.5 py-1.5 text-xs font-semibold shadow-sm hover:shadow-md transition-shadow ${c.brandClasses}`}
            data-testid={`invite-channel-${c.id}`}
          >
            {c.id === "sms" && <MessageSquare size={12} />}
            {c.label}
          </a>
        ))}
      </div>

      <p className="mt-2 text-[11px] font-mono text-gray-400">
        Each link is personalised — when someone signs up through it, we mark them as <strong className="font-semibold">Joined</strong> in your history below.
      </p>
    </div>
  );
}
